/**
 * Trenching & Excavation Evaluation & Work Plan Component
 * =========================================================
 *
 * Individual form component
 *
 * Base Components:
 * - EXTENDS BaseFormComponent<TrenchingExcavationWorkPlan> for form logic inheritance
 * - USES FormLayoutComponent for step navigation UI (in template)
 *
 * Form Configuration:
 * - Form structure, steps, sections, and field organization are defined in the form model
 * - Component implements abstract methods: initializeForm() and getFormData()
 * - Component overrides setupDefaultStepValidation() for custom step validation rules
 *
 * Key Functionality:
 * - Creates 3 nested form groups: generalInfo, siteConditions, accessShoringCompletion
 * - Optional checkbox groups for equipment used, underground installations, atmospheric monitoring, and trench access/egress
 * - Conditional field display and validation:
 *     hydraulicShoringMfr / hydraulicShoringSerialNumber — required when hydraulicShoringInstalled
 *     engineeredSystemEngineerName — required when engineeredSystem
 *     undergroundOtherDescription — required when undergroundOther
 *     ventilationFansOther — required when ventilationFans = 'Other'
 * - Required acknowledgement checkboxes for inspections and training (simpleCheckboxValidator)
 * - Flattens nested form structure for backend submission
 *
 * Form Model vs Component Responsibilities:
 * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
 * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
 *
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { TrenchingExcavationWorkPlan } from '../../../../../models/forms/trenching-excavation-work-plan/trenching-excavation-work-plan.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { simpleCheckboxValidator } from '../../../../../utils/form-validators';

@Component({
  selector: 'app-trenching-excavation-work-plan',
  templateUrl: './trenching-excavation-work-plan.component.html',
  styleUrls: ['./trenching-excavation-work-plan.component.css'],
})
export class TrenchingExcavationWorkPlanComponent
  extends BaseFormComponent<TrenchingExcavationWorkPlan>
  implements OnInit
{
  override formType = TrenchingExcavationWorkPlan.getFormInfo().formType;
  override model = new TrenchingExcavationWorkPlan();
  override steps: FormStep[] = TrenchingExcavationWorkPlan.getFormSteps();

  equipmentUsedCheckboxes: { name: string; label: string }[] = [];
  undergroundInstallationsCheckboxes: { name: string; label: string }[] = [];
  atmosphericMonitoringCheckboxes: { name: string; label: string }[] = [];
  trenchAccessCheckboxes: { name: string; label: string }[] = [];
  rpeCheckboxes: { name: string; label: string }[] = [];
  soilsEngineerCheckboxes: { name: string; label: string }[] = [];
  inspectionsCheckboxes: { name: string; label: string }[] = [];
  trainingCheckboxes: { name: string; label: string }[] = [];

  readonly soilClassificationOptions = ['Class A', 'Class B', 'Class C', 'Stable Rock'];
  readonly yesNoOptions = ['Yes', 'No'];
  readonly yesNoOtherOptions = ['Yes', 'No', 'Other'];

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(fb, formService, router, entraService, attachmentService, toastService, signatureService, screenSizeService);
    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  protected override initializeForm(): void {
    this.equipmentUsedCheckboxes = this.getEquipmentUsedCheckboxes();
    this.undergroundInstallationsCheckboxes = this.getUndergroundInstallationsCheckboxes();
    this.atmosphericMonitoringCheckboxes = [{ name: 'continuousAtmosphericMonitoring', label: this.fieldMappings['continuousAtmosphericMonitoring'] }];
    this.trenchAccessCheckboxes = this.getTrenchAccessCheckboxes();
    this.rpeCheckboxes = [{ name: 'rpeStampedDrawing', label: this.fieldMappings['rpeStampedDrawing'] }];
    this.soilsEngineerCheckboxes = [{ name: 'soilsEngineerApproval', label: this.fieldMappings['soilsEngineerApproval'] }];
    this.inspectionsCheckboxes = [{ name: 'inspectionsAcknowledged', label: this.fieldMappings['inspectionsAcknowledged'] }];

    const equipmentUsedGroup = this.fb.group({});
    this.equipmentUsedCheckboxes.forEach(cb => {
      equipmentUsedGroup.addControl(cb.name, this.fb.control(this.model[cb.name as keyof TrenchingExcavationWorkPlan] || false));
    });

    const undergroundInstallationsGroup = this.fb.group({});
    this.undergroundInstallationsCheckboxes.forEach(cb => {
      undergroundInstallationsGroup.addControl(cb.name, this.fb.control(this.model[cb.name as keyof TrenchingExcavationWorkPlan] || false));
    });

    const atmosphericMonitoringGroup = this.fb.group({
      continuousAtmosphericMonitoring: [this.model.continuousAtmosphericMonitoring],
    });

    const trenchAccessGroup = this.fb.group({});
    this.trenchAccessCheckboxes.forEach(cb => {
      const defaultValue = cb.name === 'accessLadder' ? true : (this.model[cb.name as keyof TrenchingExcavationWorkPlan] || false);
      trenchAccessGroup.addControl(cb.name, this.fb.control(defaultValue));
    });

    // const rpeGroup = this.fb.group({ rpeStampedDrawing: [this.model.rpeStampedDrawing] });
    // const soilsEngineerGroup = this.fb.group({ soilsEngineerApproval: [this.model.soilsEngineerApproval] });

    const inspectionsGroup = this.fb.group(
      { inspectionsAcknowledged: [true] },
      { validators: [simpleCheckboxValidator(1)] }
    );


    this.form = this.fb.group({
      generalInfo: this.fb.group(
        {
          employerName: ['MacDonald Miller Facility Solutions', Validators.required],
          date: [this.model.date, Validators.required],
          jobsiteNameAddress: [this.model.jobsiteNameAddress, Validators.required],
          competentPersonName: [this.model.competentPersonName, Validators.required],
          utilityLocatesObtained: [this.model.utilityLocatesObtained, Validators.required],
          authorityToStopWork: [this.model.authorityToStopWork || 'Yes', Validators.required],
        },
        { validators: this.generalInfoBlockingValidator() }
      ),
      siteConditions: this.fb.group({
        equipmentUsed: equipmentUsedGroup,
        hydraulicShoringMfr: [this.model.hydraulicShoringMfr],
        hydraulicShoringSerialNumber: [this.model.hydraulicShoringSerialNumber],
        engineeredSystemEngineerName: [this.model.engineeredSystemEngineerName],
        soilClassification: ['Class C', Validators.required],
        undergroundInstallations: undergroundInstallationsGroup,
        undergroundOtherDescription: [this.model.undergroundOtherDescription],
        undergroundInstallationProtectionMethod: [this.model.undergroundInstallationProtectionMethod],
        atmosphericMonitoring: atmosphericMonitoringGroup,
        ventilationFans: [this.model.ventilationFans],
        ventilationFansOther: [this.model.ventilationFansOther],
        surfaceEncumbranceProtectionMethod: [this.model.surfaceEncumbranceProtectionMethod, Validators.required],
        adjacentStructureStabilizationMethod: [this.model.adjacentStructureStabilizationMethod, Validators.required],
        hazardProtectionMethod: [this.model.hazardProtectionMethod, Validators.required],
      }),
      accessShoringCompletion: this.fb.group({
        access: trenchAccessGroup,
        accessEgressOtherDescription: [this.model.accessEgressOtherDescription],
        shoringPlanDescription: [this.model.shoringPlanDescription, Validators.required],
        // rpeDrawing: rpeGroup,
        // soilsApproval: soilsEngineerGroup,
        inspections: inspectionsGroup,
        trainedEmployees: [this.model.trainedEmployees, Validators.required],
      }),
    });

    this.setupConditionalValidators();
  }

  private setupConditionalValidators(): void {
    // Hydraulic shoring MFR and serial # required when hydraulicShoringInstalled is checked
    this.equipmentUsedFormGroup.get('hydraulicShoringInstalled')?.valueChanges.subscribe(checked => {
      const mfr = this.siteConditionsFormGroup.get('hydraulicShoringMfr');
      const serial = this.siteConditionsFormGroup.get('hydraulicShoringSerialNumber');
      checked ? mfr?.setValidators([Validators.required]) : mfr?.clearValidators();
      checked ? serial?.setValidators([Validators.required]) : serial?.clearValidators();
      mfr?.updateValueAndValidity();
      serial?.updateValueAndValidity();
    });

    // Engineer name required when engineeredSystem is checked
    this.equipmentUsedFormGroup.get('engineeredSystem')?.valueChanges.subscribe(checked => {
      const control = this.siteConditionsFormGroup.get('engineeredSystemEngineerName');
      checked ? control?.setValidators([Validators.required]) : control?.clearValidators();
      control?.updateValueAndValidity();
    });

    // Underground other description required when undergroundOther is checked
    this.undergroundInstallationsFormGroup.get('undergroundOther')?.valueChanges.subscribe(checked => {
      const control = this.siteConditionsFormGroup.get('undergroundOtherDescription');
      checked ? control?.setValidators([Validators.required]) : control?.clearValidators();
      control?.updateValueAndValidity();
    });

    // Ventilation fans other description required when ventilationFans = 'Other'
    this.siteConditionsFormGroup.get('ventilationFans')?.valueChanges.subscribe(value => {
      const control = this.siteConditionsFormGroup.get('ventilationFansOther');
      value === 'Other' ? control?.setValidators([Validators.required]) : control?.clearValidators();
      control?.updateValueAndValidity();
    });
  }

  protected override setupDefaultStepValidation(): void {
    if (!this.shouldApplyFormRequirements()) {
      return;
    }

    this.setupStepValidation('general-info', this.generalInfoFormGroup);
    this.setupStepValidation('site-conditions', this.siteConditionsFormGroup);
    this.setupStepValidation('access-shoring-completion', this.accessShoringCompletionFormGroup);
  }

  // Checkbox data getters
  getEquipmentUsedCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'hydraulicShoringInstalled', label: this.fieldMappings['hydraulicShoringInstalled'] },
      { name: 'trenchBox', label: this.fieldMappings['trenchBox'] },
      { name: 'engineeredSystem', label: this.fieldMappings['engineeredSystem'] },
    ];
  }

  getUndergroundInstallationsCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'undergroundGas', label: this.fieldMappings['undergroundGas'] },
      { name: 'undergroundWater', label: this.fieldMappings['undergroundWater'] },
      { name: 'undergroundSewer', label: this.fieldMappings['undergroundSewer'] },
      { name: 'undergroundElectric', label: this.fieldMappings['undergroundElectric'] },
      { name: 'undergroundCommunications', label: this.fieldMappings['undergroundCommunications'] },
      { name: 'undergroundOther', label: this.fieldMappings['undergroundOther'] },
    ];
  }

  getTrenchAccessCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'accessLadder', label: this.fieldMappings['accessLadder'] },
      { name: 'accessTaperSlope', label: this.fieldMappings['accessTaperSlope'] },
      { name: 'accessStairs', label: this.fieldMappings['accessStairs'] },
    ];
  }

  private generalInfoBlockingValidator(): ValidatorFn {
    return (group: AbstractControl) => {
      if (group.get('utilityLocatesObtained')?.value === 'No') {
        return { locatesNotObtained: true };
      }
      if (group.get('authorityToStopWork')?.value === 'No') {
        return { noAuthorityToStopWork: true };
      }
      return null;
    };
  }

  // Form group getters
  get generalInfoFormGroup(): FormGroup { return this.form.get('generalInfo') as FormGroup; }
  get siteConditionsFormGroup(): FormGroup { return this.form.get('siteConditions') as FormGroup; }
  get equipmentUsedFormGroup(): FormGroup { return this.siteConditionsFormGroup.get('equipmentUsed') as FormGroup; }
  get undergroundInstallationsFormGroup(): FormGroup { return this.siteConditionsFormGroup.get('undergroundInstallations') as FormGroup; }
  get atmosphericMonitoringFormGroup(): FormGroup { return this.siteConditionsFormGroup.get('atmosphericMonitoring') as FormGroup; }
  get accessShoringCompletionFormGroup(): FormGroup { return this.form.get('accessShoringCompletion') as FormGroup; }
  get trenchAccessFormGroup(): FormGroup { return this.accessShoringCompletionFormGroup.get('access') as FormGroup; }
  get rpeFormGroup(): FormGroup { return this.accessShoringCompletionFormGroup.get('rpeDrawing') as FormGroup; }
  get soilsEngineerFormGroup(): FormGroup { return this.accessShoringCompletionFormGroup.get('soilsApproval') as FormGroup; }
  get inspectionsFormGroup(): FormGroup { return this.accessShoringCompletionFormGroup.get('inspections') as FormGroup; }
  get trainingFormGroup(): FormGroup { return this.accessShoringCompletionFormGroup.get('training') as FormGroup; }

  protected override getFormData(): TrenchingExcavationWorkPlan {
    const fv = this.form.value;

    const flattenedData = {
      ...fv.generalInfo,
      ...fv.siteConditions.equipmentUsed,
      hydraulicShoringMfr: fv.siteConditions.hydraulicShoringMfr,
      hydraulicShoringSerialNumber: fv.siteConditions.hydraulicShoringSerialNumber,
      engineeredSystemEngineerName: fv.siteConditions.engineeredSystemEngineerName,
      soilClassification: fv.siteConditions.soilClassification,
      ...fv.siteConditions.undergroundInstallations,
      undergroundOtherDescription: fv.siteConditions.undergroundOtherDescription,
      undergroundInstallationProtectionMethod: fv.siteConditions.undergroundInstallationProtectionMethod,
      ...fv.siteConditions.atmosphericMonitoring,
      ventilationFans: fv.siteConditions.ventilationFans,
      ventilationFansOther: fv.siteConditions.ventilationFansOther,
      surfaceEncumbranceProtectionMethod: fv.siteConditions.surfaceEncumbranceProtectionMethod,
      adjacentStructureStabilizationMethod: fv.siteConditions.adjacentStructureStabilizationMethod,
      hazardProtectionMethod: fv.siteConditions.hazardProtectionMethod,
      ...fv.accessShoringCompletion.access,
      accessEgressOtherDescription: fv.accessShoringCompletion.accessEgressOtherDescription,
      shoringPlanDescription: fv.accessShoringCompletion.shoringPlanDescription,
      ...fv.accessShoringCompletion.rpeDrawing,
      ...fv.accessShoringCompletion.soilsApproval,
      ...fv.accessShoringCompletion.inspections,
      ...fv.accessShoringCompletion.training,
      trainedEmployees: fv.accessShoringCompletion.trainedEmployees,
    };

    const model = new TrenchingExcavationWorkPlan(flattenedData);
    return this.updateModelWithAttachments(model);
  }
}
